<template>
	<div>
		<!-- Headline -->
		<div slot="headline" class="flex xs-12 fs5 lh125 fw7 c1 mb2">
			<div class="flex xs-6">{{ $t("beneficiaries") }}</div>
			<div class="flex xs-6 xs-right x2">
				<j-button class="tertiary" @click="$modal.show('addBeneficiary')">
					<crm-icon name="add" size="normal" color="c2" />
					{{ $t("add") }}
				</j-button>
			</div>
		</div>

		<!-- List of beneficiaries -->
		<j-expendable-box
			v-for="(item, i) in computedList"
			:key="i"
			class="bg5 pl3 fs3 mt2 r2 b1 bc7"
		>
			<div slot="header" class="c2 fw7">{{ item.entity.name || "-----" }}</div>
			<div slot="body" class="flex wrap xs-12 pb4 pr4 mt4">
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("ratio") }}</div>
					<div class="flex xs-6 xs-right">
						{{ Number(item.ratio * 100).toFixed(2) }}%
					</div>
				</div>
				<div class="flex xs-12 mt6">
					<div class="flex xs-6 c6 fw7">{{ $t("type") }}</div>
					<div class="flex xs-6 xs-right">{{ item.notes }}</div>
				</div>
				<div class="flex xs-12 xs-right mt6 bt1 bc7">
					<j-button
						class="error mt2 ml2"
						size="small"
						@click="removeBeneficiary(item.id)"
						>{{ $t("remove") }}</j-button
					>
				</div>
			</div>
		</j-expendable-box>

		<!-- no data -->
		<j-card class="mb2 bg4--l3 p4 ta2 fs3 c6" v-if="computedList.length === 0">
			{{ $t("errors.no_data") }}
		</j-card>

		<!-- add beneficiary -->
		<core-modal name="addBeneficiary" :headline="headline">
			<div class="p4">
				<!-- Select Individual entity as Beneficiary -->
				<span class="xs-12 fw7 lh125 fs4">{{ $t("entity") }}</span>
				<div
					@click="$modal.show('selectEntity')"
					class="xs-12 r2 pt3 pb3 cu2 beneficiary-value"
				>
					{{ entity.name ? entity.name : "---" }}
					<div
						class="flex fs3 p2 fw7 c6 xs-center xs-middle case-management-close"
						@click.stop="removeEntity"
					>
						✕
					</div>
				</div>

				<!-- Ratio -->
				<core-form-input
					componentName="j-base-input-number"
					name="ratio"
					class="mt6"
					:label="$t('ratio')"
					v-validate="'required'"
					v-model="ratio"
					:validationErrors="errors"
				/>

				<!-- isRevocable -->
				<core-form-input
					componentName="j-base-toggle"
					name="isRevocable"
					class="mt6"
					:label="$t('is_revocable')"
					v-model="isRevocable"
				/>

				<core-form-input
					componentName="j-base-select"
					:options="options"
					name="type"
					class="mt6"
					:label="$t('type')"
					v-model="type"
				/>

				<!-- Relationsip -->
				<core-form-input
					componentName="j-base-input-text"
					name="ratio"
					class="mt6"
					:label="$t('relationship_with_insured')"
					v-validate="'required'"
					v-model="relationship"
					:validationErrors="errors"
				/>

				<div class="flex xs-12 xs-right mt6 bt1 bc7">
					<j-button class="primary mt2" size="small" @click="handleAdd">{{
						$t("add")
					}}</j-button>
				</div>
			</div>
		</core-modal>

		<!-- Select entity for beneficiary -->
		<core-modal
			name="selectEntity"
			headline="Select Entity"
			:pivotX="0"
			:pivotY="0"
		>
			<div style="height:100vh; overflow: scroll;">
				<entity-picker
					:allowedEntityTypes="['individual']"
					@entity:selected="handleEntitySelect"
				/>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import EntityPicker from "@/modules/caseManagement/components/CaseForm/EntityPicker/EntityPicker"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JExpendableBox from "@/components/JExpendableBox"
import JCard from "@/components/JCard"
import CrmIcon from "@/components/CrmIcon"
import gql from "@/api/gql.js"

export default {
	components: {
		JButton,
		EntityPicker,
		CoreModal,
		CoreFormInput,
		JExpendableBox,
		CrmIcon,
		JCard
	},

	props: {
		caseId: { type: String, required: true },
		list: { type: Array, required: true, default: () => [] },
		insuredId: { type: String, required: true }
	},

	data() {
		return {
			entity: {
				name: null,
				id: null
			},
			ratio: 0,
			isRevocable: false,
			type: null,
			options: [
				{ name: "primaryBeneficiary", value: "primaryBeneficiary" },
				{ name: "contingentBeneficiary", value: "contingentBeneficiary" }
			],
			relationship: null
		}
	},

	methods: {
		handleEntitySelect(payload) {
			if (!payload) return
			this.entity = payload
			this.$modal.hide("selectEntity")
		},
		async handleAdd() {
			const res = await this.addBeneficiaryToCase()
			if (res) {
				await this.addRelationship()
			}
			this.$modal.hide("addBeneficiary")
			this.$emit("added")
		},
		async addBeneficiaryToCase() {
			const mutation = `
        mutation addBeneficiary($caseId:String!, $input: beneficiaryEligibilityInput!){
					addBeneficiaryEligibilityToCase(caseId: $caseId, input: $input ){
						status
            errors
					}
				}
      `
			const variables = {
				caseId: this.caseId,
				input: {
					entityId: this.entity?.id,
					ratio: this.ratio,
					notes: this.type,
					isRevocable: this.isRevocable
				}
			}
			const res = await gql({
				query: mutation,
				variables
			})
			if (res?.data?.addBeneficiaryEligibilityToCase?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addBeneficiaryEligibilityToCase?.errors ||
							"Oops ! An error occured."
					}
				})
				return null
			} else {
				return true
			}
		},
		async addRelationship() {
			const mutation = `
				mutation addRelationship($input: createLinkInput!) {
					addLink(linkInput: $input) {
						status
						errors
					}
				}
			`
			const res = await gql({
				query: mutation,
				variables: {
					input: {
						sourceId: this.insuredId,
						link: this.relationship,
						targetId: this.entity?.id
					}
				}
			})
			if (res?.data?.addLink?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addLink?.errors ||
							"Oops ! An error occured."
					}
				})
				return null
			}
		},
		async removeBeneficiary(entityId) {
			const mutation = `
        mutation removeBeneficiary($caseId:String!, $beneficiaryEligibilityId: String!){
          removeBeneficiaryEligibilityFromCase(caseId:$caseId, beneficiaryEligibilityId: $beneficiaryEligibilityId){
            status
          }
        }
      `
			const variables = {
				caseId: this.caseId,
				beneficiaryEligibilityId: entityId
			}
			const res = await gql({
				query: mutation,
				variables
			})
			if (
				res?.data?.removeBeneficiaryEligibilityFromCase?.status !== "success"
			) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeBeneficiaryEligibilityFromCase?.errors ||
							"Oops ! An error occured."
					}
				})
			} else this.$emit("removed")
		}
	},

	computed: {
		computedList() {
			return (this.list || []).map(item => ({
				id: item.id,
				ratio: item.ratio,
				notes: item.notes,
				entity: item.entity
				// {
				//   id
				//   name
				// }
			}))
		}
	}
}
</script>

<style scoped>
.beneficiary-value {
	position: relative;
	background-color: rgba(0, 0, 0, 0);
}

.beneficiary-value:hover {
	background-color: rgba(0, 0, 0, 0.06);
}

.case-management-close {
	display: none;
	width: 20px;
	height: 20px;
	border-radius: 100%;
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	background-color: rgba(0, 0, 0, 0.09);
	z-index: 10;
}

.beneficiary-value:hover .case-management-close {
	display: flex;
}
</style>
